<template>
	<RouterLink v-if="to" :class="classNames" :to>
		<slot />
	</RouterLink>

	<a v-else-if="href" :class="classNames" :href>
		<slot />
	</a>

	<button
		v-else
		:class="classNames"
		type="button"
		:disabled="disabled || formStatus === 'loading'"
	>
		<CircleDashedIcon v-if="formStatus === 'loading'" class="action-loading" />
		<slot />
	</button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { type RouteLocationRaw } from 'vue-router'

import { type FormResult } from '@/app/common/form/TForm.vue'
import CircleDashedIcon from '@/shared/icons/CircleDashedIcon.vue'

const { appearance = 'tertiary', iconOnly } = defineProps<{
	to?: RouteLocationRaw
	href?: string
	appearance?: 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'link'
	iconOnly?: 'mobile'
	formStatus?: FormResult['status']
	disabled?: boolean
}>()

const classNames = computed(() => [
	'action',
	`action--${appearance}`,
	{ [`action--icon-only-${iconOnly}`]: iconOnly },
])
</script>

<style scoped>
.action,
:global(::file-selector-button) {
	--action-border-size: var(--size-2);

	align-items: center;
	border: var(--action-border-size) solid transparent;
	border-radius: var(--border-radius-md);
	display: inline flex;
	font: var(--type-body-m-medium);
	gap: var(--size-8);
	justify-content: center;
	min-block-size: var(--button-block-size);
	padding-block: calc(var(--size-10) - var(--action-border-size));
	padding-inline: calc(var(--size-16) - var(--action-border-size));
	text-decoration: none;
}

/* TODO: Nest into base once https://github.com/vuejs/core/issues/12405 is resolved. */
.action:disabled,
:global(:disabled::file-selector-button) {
	color: var(--c-button--disabled);
}

.action--primary {
	background-color: var(--c-button-primary);
	color: var(--c-text-primary-dark);

	&:where(:not(:disabled)):hover {
		background-color: var(--c-button-primary--hover);
	}

	&:disabled {
		background-color: var(--c-button-primary--disabled);
	}
}

.action--secondary {
	background-color: var(--c-button-secondary);
	color: var(--c-text-invert);

	&:where(:not(:disabled)):hover {
		background-color: var(--c-button-secondary--hover);
	}

	&:disabled {
		background-color: var(--c-button-secondary--disabled);
	}
}

.action--tertiary {
	border-color: var(--c-button-tertiary);
	color: var(--c-button-tertiary);

	&:where(:not(:disabled)):hover {
		background-color: var(--c-button-tertiary--hover);
	}

	&:disabled {
		border-color: var(--c-button-tertiary--disabled);
	}
}

/* TODO: Nest into tertiary once https://github.com/vuejs/core/issues/12405 is resolved. */
:global(::file-selector-button) {
	border-color: var(--c-button-tertiary);
	color: var(--c-button-tertiary);
}

/* TODO: Nest into tertiary once https://github.com/vuejs/core/issues/12405 is resolved. */
:global(:enabled::file-selector-button:hover) {
	background-color: var(--c-button-tertiary--hover);
}

/* TODO: Nest into tertiary once https://github.com/vuejs/core/issues/12405 is resolved. */
:global(:disabled::file-selector-button) {
	border-color: var(--c-button-tertiary--disabled);
}

.action--ghost {
	color: var(--c-button-tertiary);

	&:where(:not(:disabled)):hover {
		background-color: var(--c-button-tertiary--hover);
	}
}

.action--link {
	border: none;
	color: var(--c-button-tertiary);
	min-block-size: unset;
	padding: 0;

	&:where(:not(:disabled)):hover {
		text-decoration: underline;
	}
}

@container (inline-size < 32rem) {
	.action--icon-only-mobile > :not(.icon) {
		display: none;
	}
}

.action-loading {
	animation: rotate 2.5s linear infinite;
}

@keyframes rotate {
	to {
		rotate: 1turn;
	}
}
</style>
